import { Component, ErrorInfo, ReactNode } from 'react';
import bugsnag from '../lib/bugsnag';

interface Props {
  children: ReactNode;
  reelId?: string;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorCount: number;
  lastErrorTime: number;
}

class ReelCardErrorBoundary extends Component<Props, State> {
  private static readonly MAX_ERRORS = 3;
  private static readonly ERROR_RESET_INTERVAL = 30000; // 30 seconds

  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      errorCount: 0,
      lastErrorTime: 0
    };
  }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const now = Date.now();
    const { errorCount, lastErrorTime } = this.state;
    
    // Check if we should reset error count (after 30 seconds)
    const shouldResetCount = now - lastErrorTime > ReelCardErrorBoundary.ERROR_RESET_INTERVAL;
    const newErrorCount = shouldResetCount ? 1 : errorCount + 1;
    
    console.error('ReelCard error:', error, errorInfo);
    
    // Log error to Bugsnag
    bugsnag.notify(error, (event) => {
      event.addMetadata('errorInfo', {
        componentStack: errorInfo.componentStack,
        errorBoundary: 'ReelCardErrorBoundary',
        reelId: this.props.reelId,
        errorCount: newErrorCount,
        maxErrors: ReelCardErrorBoundary.MAX_ERRORS,
      });
      event.addMetadata('react', {
        errorBoundary: 'ReelCardErrorBoundary',
        errorInfo: errorInfo,
        reelId: this.props.reelId,
      });
      event.addMetadata('reel', {
        reelId: this.props.reelId,
        errorCount: newErrorCount,
        isMaxErrorsReached: newErrorCount >= ReelCardErrorBoundary.MAX_ERRORS,
      });
    });
    
    this.setState({ 
      error, 
      errorCount: newErrorCount,
      lastErrorTime: now
    });
  }

  resetError = () => {
    this.setState({ 
      hasError: false, 
      error: undefined, 
      errorCount: 0,
      lastErrorTime: 0
    });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    const { hasError, errorCount } = this.state;
    
    if (hasError) {
      // If we've hit too many errors, show a permanent error state
      if (errorCount >= ReelCardErrorBoundary.MAX_ERRORS) {
        return (
          <div className="bg-gray-800 rounded-lg p-4 border border-red-700 min-h-[200px] flex items-center justify-center">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-500/30 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <p className="text-gray-400 text-sm mb-2">Post failed to load</p>
              {this.props.reelId && (
                <p className="text-gray-500 text-xs mb-3">ID: {this.props.reelId}</p>
              )}
              <p className="text-gray-500 text-xs mb-3">Too many errors - please refresh the page</p>
              <button
                onClick={() => window.location.reload()}
                className="text-blue-400 hover:text-blue-300 text-xs underline"
              >
                Refresh page
              </button>
            </div>
          </div>
        );
      }

      // Normal error state with retry
      return (
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 min-h-[200px] flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <p className="text-gray-400 text-sm mb-2">Failed to load post</p>
            {this.props.reelId && (
              <p className="text-gray-500 text-xs mb-3">ID: {this.props.reelId}</p>
            )}
            <p className="text-gray-500 text-xs mb-3">Error {errorCount}/{ReelCardErrorBoundary.MAX_ERRORS}</p>
            <button
              onClick={this.resetError}
              className="text-blue-400 hover:text-blue-300 text-xs underline"
            >
              Try again
            </button>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-3 text-left">
                <summary className="text-gray-500 cursor-pointer text-xs">Error Details</summary>
                <pre className="text-xs text-red-400 mt-1 bg-gray-900 p-2 rounded overflow-auto">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ReelCardErrorBoundary;
